import { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../ui/components/ui/dialog";
import { useNavigate, useParams } from "react-router-dom";
import { useUser } from "../../hooks/use-user";
import useApiRequest from "../../lib/hooks/useRequest";
import {
  IPatientRequestDetails,
  IPatientRequestListEntities,
  IPatientRequestPublicLocation,
  IPatientRequestStatusOption,
} from "../../lib/interfaces/patient-request";
import NoPermissionRead from "../../ui/components/ui/NoPermission";

export const PatientRequestDetailParent = () => {
  const { user } = useUser();
  if (!user) return null;
  return (
    <>
      {user.permissionApplicants === "NONE" && !user.permissionOrganizationAdmin ? (
        <NoPermissionRead title="Patientenanfragen" />
      ) : (
        <PatientRequestDetail />
      )}
    </>
  );
};

/**
 * Die Einzelansicht einer Anfrage: alle erfassten Felder, die vier Phasen als Schalter,
 * das Notizfeld und das Löschen (Issue KAN-229).
 *
 * Wer nur Lese-Recht hat, sieht alles, kann aber nichts ändern. Der Server weist eine
 * Änderung ohnehin ab; die Oberfläche zeigt sie erst gar nicht an.
 */
const PatientRequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { apiRequest } = useApiRequest();
  const { user } = useUser();

  const [anfrage, setAnfrage] = useState<IPatientRequestDetails | null>(null);
  const [statusOptionen, setStatusOptionen] = useState<IPatientRequestStatusOption[]>([]);
  const [standorte, setStandorte] = useState<IPatientRequestPublicLocation[]>([]);
  // Der gewaehlte, noch nicht gespeicherte Standort. Das Umhaengen passiert erst beim
  // Speichern — es verschickt eine Mail an den neuen Standort, und das soll niemandem
  // beim blossen Durchklicken der Auswahl passieren.
  const [standortEntwurf, setStandortEntwurf] = useState("");
  const [umhaengenFehlgeschlagen, setUmhaengenFehlgeschlagen] = useState(false);
  const [loeschdialogOffen, setLoeschdialogOffen] = useState(false);
  const [notiz, setNotiz] = useState("");
  const [laedt, setLaedt] = useState(true);
  const [speichert, setSpeichert] = useState(false);
  const [notizGespeichert, setNotizGespeichert] = useState(false);
  const [notizFehlgeschlagen, setNotizFehlgeschlagen] = useState(false);

  const darfAendern =
    user?.permissionOrganizationAdmin || user?.permissionApplicants === "WRITE" || user?.permissionApplicants === "FULL";

  const laden = useCallback(async () => {
    setLaedt(true);
    const [detail, entities] = await Promise.all([
      apiRequest<IPatientRequestDetails>(`patient-requests/${id}`, "GET"),
      apiRequest<IPatientRequestListEntities>("patient-requests/list-entities", "GET"),
    ]);
    if (detail.data) {
      setAnfrage(detail.data);
      setNotiz(detail.data.notes ?? "");
      setStandortEntwurf(detail.data.belongsToCompanyLocationId ?? "");
    }
    if (entities.data?.status) setStatusOptionen(entities.data.status);
    if (entities.data?.companyLocations) setStandorte(entities.data.companyLocations);
    setLaedt(false);
  }, [apiRequest, id]);

  useEffect(() => {
    void laden();
  }, [laden]);

  const statusWechseln = async (statusId: string) => {
    if (!darfAendern || statusId === anfrage?.belongsToStatus) return;
    setSpeichert(true);
    const antwort = await apiRequest<IPatientRequestDetails>(`patient-requests/${id}`, "PATCH", {
      body: { belongsToStatus: statusId },
    });
    if (antwort.data) setAnfrage(antwort.data);
    setSpeichert(false);
  };

  const standortGeaendert = Boolean(standortEntwurf) && standortEntwurf !== anfrage?.belongsToCompanyLocationId;

  /** Speichert Notiz und, falls gewaehlt, den neuen Standort — in einem Zug. */
  const speichern = async () => {
    if (!darfAendern) return;
    setSpeichert(true);
    setUmhaengenFehlgeschlagen(false);
    setNotizFehlgeschlagen(false);

    let stand = anfrage;

    // Zuerst umhaengen: Scheitert das, bleibt die Notiz ungespeichert und der Nutzer
    // sieht, dass nichts passiert ist, statt eine halb erledigte Aenderung vorzufinden.
    if (standortGeaendert) {
      const umgehaengt = await apiRequest<IPatientRequestDetails>(`patient-requests/${id}/assignment`, "PATCH", {
        body: { belongsToCompanyLocationId: standortEntwurf },
      });
      if (!umgehaengt.data) {
        setUmhaengenFehlgeschlagen(true);
        setSpeichert(false);
        return;
      }
      stand = umgehaengt.data;
    }

    const antwort = await apiRequest<IPatientRequestDetails>(`patient-requests/${id}`, "PATCH", {
      body: { notes: notiz },
    });

    // Ein gescheitertes Speichern darf nie wie ein erfolgreiches aussehen. Der Standort
    // kann vorher schon umgehaengt worden sein, deshalb wird der Stand trotzdem uebernommen.
    if (!antwort.data) {
      if (stand) {
        setAnfrage(stand);
        setStandortEntwurf(stand.belongsToCompanyLocationId ?? "");
      }
      setNotizFehlgeschlagen(true);
      setSpeichert(false);
      return;
    }

    stand = antwort.data;
    setAnfrage(stand);
    setStandortEntwurf(stand.belongsToCompanyLocationId ?? "");
    setNotizGespeichert(true);
    setTimeout(() => setNotizGespeichert(false), 2500);
    setSpeichert(false);
  };

  const loeschen = async () => {
    if (!darfAendern) return;
    setSpeichert(true);
    const antwort = await apiRequest(`patient-requests/${id}`, "DELETE");
    setSpeichert(false);
    setLoeschdialogOffen(false);
    if (!antwort.error) navigate("/patientenanfragen");
  };

  if (laedt) return <div className="p-6 text-sm text-gray-500">Anfrage wird geladen…</div>;
  if (!anfrage) return <div className="p-6 text-sm text-gray-500">Diese Anfrage gibt es nicht.</div>;

  const datum = (wert?: string | null): string =>
    wert
      ? new Date(wert).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
      : "—";

  return (
    <div className="p-6">
      <button className="mb-4 text-sm text-gray-500 hover:underline" onClick={() => navigate("/patientenanfragen")}>
        ← Zurück zur Übersicht
      </button>

      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h1 className="text-xl font-medium">{[anfrage.firstName, anfrage.lastName].filter(Boolean).join(" ")}</h1>
        <p className="mt-1 text-sm text-gray-500">
          Eingegangen am {datum(anfrage.receivedAt)} · {anfrage.companyLocationTitle ?? "Standort unbekannt"}
        </p>

        <div className="mt-5 inline-flex flex-wrap overflow-hidden rounded-lg border border-gray-300">
          {statusOptionen.map((option) => {
            const aktiv = option.id === anfrage.belongsToStatus;
            return (
              <button
                key={option.id}
                disabled={!darfAendern || speichert}
                onClick={() => statusWechseln(option.id)}
                className={`border-r border-gray-300 px-4 py-2 text-sm last:border-r-0 disabled:cursor-not-allowed ${
                  aktiv ? "text-white" : "bg-white text-gray-600 hover:bg-gray-50"
                }`}
                style={aktiv ? { backgroundColor: option.color } : undefined}
              >
                {option.value}
              </button>
            );
          })}
        </div>
        {!darfAendern && <p className="mt-2 text-xs text-gray-500">Sie haben nur Leserechte und können den Status nicht ändern.</p>}

        <dl className="mt-6 grid grid-cols-[132px_1fr] gap-y-2 gap-x-4 text-sm">
          <dt className="text-gray-500">Telefon</dt>
          <dd>{anfrage.phone || "—"}</dd>
          <dt className="text-gray-500">E-Mail</dt>
          <dd>{anfrage.mail || "—"}</dd>
          <dt className="text-gray-500">Standort</dt>
          <dd>
            {darfAendern ? (
              <>
                {/* Umhaengen ist der einzige Weg zurueck, wenn ein Standort archiviert
                    wurde und seine Anfragen sonst niemandem mehr gehoeren. */}
                <select
                  className="h-9 w-full max-w-sm rounded-md border border-gray-300 pl-2 pr-8 text-sm disabled:opacity-60"
                  value={standortEntwurf}
                  disabled={speichert}
                  onChange={(event) => setStandortEntwurf(event.target.value)}
                >
                  {!standorte.some((standort) => standort._id === anfrage.belongsToCompanyLocationId) && (
                    <option value={anfrage.belongsToCompanyLocationId ?? ""}>
                      {anfrage.companyLocationTitle ?? "Standort unbekannt"} (archiviert)
                    </option>
                  )}
                  {standorte.map((standort) => (
                    <option key={standort._id} value={standort._id}>
                      {standort.title}
                    </option>
                  ))}
                </select>
                {standortGeaendert && <p className="mt-1 text-xs text-gray-500">Wird beim Speichern zugeordnet, der neue Standort bekommt eine E-Mail.</p>}
                {umhaengenFehlgeschlagen && <p className="mt-1 text-xs text-red-600">Der Standort konnte nicht geändert werden. Es wurde nichts gespeichert.</p>}
              </>
            ) : (
              anfrage.companyLocationTitle ?? "—"
            )}
          </dd>
          <dt className="text-gray-500">Nachricht</dt>
          <dd className="whitespace-pre-wrap">{anfrage.message || "—"}</dd>
          <dt className="text-gray-500">Wird gelöscht</dt>
          <dd className="text-gray-500">
            {/* Ein Wort reicht: Der Status steht zwei Zeilen hoeher als Schalter, ihn hier
                zu wiederholen sagt nichts Neues. */}
            {anfrage.expiresAt ? `am ${datum(anfrage.expiresAt)}` : "Nie"}
          </dd>
          {anfrage.mailDeliveryFailedAt && (
            <>
              <dt className="text-gray-500">Benachrichtigung</dt>
              <dd className="text-red-600">Versand fehlgeschlagen am {datum(anfrage.mailDeliveryFailedAt)}</dd>
            </>
          )}
        </dl>

        <div className="mt-6">
          <label className="mb-1 block text-sm text-gray-700" htmlFor="notiz">
            Notiz
          </label>
          <textarea
            id="notiz"
            className="min-h-[88px] w-full rounded-md border border-gray-300 p-2.5 text-sm disabled:bg-gray-50"
            placeholder="Was wurde besprochen?"
            value={notiz}
            disabled={!darfAendern}
            onChange={(event) => setNotiz(event.target.value)}
          />
          {darfAendern && (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <button
                className="rounded-md bg-[#0099ce] px-5 py-2 text-sm font-medium text-white disabled:opacity-60"
                disabled={speichert}
                onClick={speichern}
              >
                Speichern
              </button>
              {/* Loeschen ist endgueltig und traf bisher mit einem Klick. Zwischen Absicht
                  und Vollzug gehoert eine Frage, die auch sagt, was genau verschwindet. */}
              <Dialog open={loeschdialogOffen} onOpenChange={setLoeschdialogOffen}>
                <DialogTrigger asChild>
                  <button
                    className="rounded-md border border-gray-300 px-5 py-2 text-sm text-gray-700 disabled:opacity-60"
                    disabled={speichert}
                  >
                    Anfrage löschen
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Diese Anfrage löschen?</DialogTitle>
                    <DialogDescription>
                      Name, Kontaktdaten und Nachricht werden entfernt. Das lässt sich nicht rückgängig machen.
                    </DialogDescription>
                  </DialogHeader>
                  <p className="text-sm text-gray-500">
                    Für die Auswertung bleibt die Anfrage als Zahl erhalten, ohne jeden Bezug zur Person. Ihre Zahlen stimmen
                    danach also weiterhin.
                  </p>
                  <DialogFooter className="gap-2 sm:gap-2">
                    <button
                      className="rounded-md border border-gray-300 px-5 py-2 text-sm text-gray-700"
                      onClick={() => setLoeschdialogOffen(false)}
                    >
                      Abbrechen
                    </button>
                    <button
                      className="rounded-md bg-red-600 px-5 py-2 text-sm font-medium text-white disabled:opacity-60"
                      disabled={speichert}
                      onClick={loeschen}
                    >
                      Endgültig löschen
                    </button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              {notizGespeichert && <span className="text-sm text-green-700">Gespeichert</span>}
              {notizFehlgeschlagen && <span className="text-sm text-red-600">Die Notiz wurde nicht gespeichert. Bitte noch einmal versuchen.</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
